import { MouseEvent, FC, useState, useEffect, useRef } from 'react'
import { cn } from '@wellesley/ui'

type MediaTooltipProps = {
  title?: string
  children: React.ReactElement
}

const DELAY = 2000
const POSITION_ADJUST = 10

const MediaTooltip: FC<MediaTooltipProps> = ({ title, children }) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const setTimer = () => {
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true)
    }, DELAY)
  }

  const handleMouseEnter = () => {
    title && setTimer()
  }

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>): void => {
    const element = wrapperRef.current
    if (!title || !element) return

    if (showTooltip) {
      setShowTooltip(false)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
        setTimer()
      }
    }

    const globalX = e.clientX
    const globalY = e.clientY

    const transformedParent = element.closest("[style*='transform']")

    let correctedX = globalX
    let correctedY = globalY

    if (transformedParent) {
      const rect = transformedParent.getBoundingClientRect()
      correctedX = globalX - rect.left
      correctedY = globalY - rect.top
    }

    setPosition({ x: correctedX, y: correctedY })
  }

  const handleMouseLeave = (): void => {
    if (!title) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setShowTooltip(false)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div
      ref={wrapperRef}
      className="relative h-full transform-none"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {title && showTooltip && (
        <div
          style={{
            top: position.y + POSITION_ADJUST,
            left: position.x + POSITION_ADJUST,
          }}
          className="fade-in animate-in zoom-in-95 fixed z-[1] flex max-w-[240px] items-center justify-center !transition-none ease-in-out"
        >
          <div
            className={cn(
              'bg-alpha-black-90 px-xs py-xxxs w-fit overflow-hidden text-ellipsis rounded-[3px] border border-white text-gray-50 shadow-[0px_0px_12px_0px_var(--alpha-black-10)]',
            )}
          >
            {title}
          </div>
        </div>
      )}
    </div>
  )
}

export default MediaTooltip
